/**
 * THE state-chip palette — one table, every state surface.
 *
 * The law: a coloured surface is a SOLID light + `dark:` pair. A `/opacity`
 * tint washes out on the dark shell, and a light-only class ships a bright slab
 * into it. Four separate state tables (job, visit, proposal, event) each grew
 * their own copy of these strings and three of them never gained dark pairs —
 * hoisting them here is what stops that drift: a new state picks a NAME, and
 * the pair comes with it.
 *
 * Tailwind statically scans these literals, so every class must stay spelled
 * out — never build one by interpolation.
 */
/* Depth (the UI-refresh mock, approved 2026-08-09): light -100/-300/-800,
   dark -900/-700/-200 — the old -50 washes read anemic on white cards, and a
   healthy list rendered as a wall of grey. */
export const CHIP = {
  neutral: 'bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600',
  /** Neutral, dimmer text — for states that are over and uninteresting (lost). */
  neutralDim: 'bg-slate-100 text-slate-600 border-slate-300 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-600',
  sky: 'bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-900 dark:text-sky-200 dark:border-sky-700',
  blue: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-700',
  indigo: 'bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-900 dark:text-indigo-200 dark:border-indigo-700',
  violet: 'bg-violet-100 text-violet-800 border-violet-300 dark:bg-violet-900 dark:text-violet-200 dark:border-violet-700',
  amber: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900 dark:text-amber-200 dark:border-amber-700',
  orange: 'bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900 dark:text-orange-200 dark:border-orange-700',
  green: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900 dark:text-green-200 dark:border-green-700',
  emerald: 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900 dark:text-emerald-200 dark:border-emerald-700',
  teal: 'bg-teal-100 text-teal-800 border-teal-300 dark:bg-teal-900 dark:text-teal-200 dark:border-teal-700',
  red: 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900 dark:text-red-200 dark:border-red-700',
  muted: 'bg-muted text-muted-foreground border-border',
} as const

/** The same palette as a WORD — the state named in its colour, no surface.
 *  For a state that is one fact among others on a row (a pill there costs the
 *  row its title). Light -700, dark -400: text contrast on a card. */
export const INK = {
  neutral: 'text-slate-700 dark:text-slate-300',
  neutralDim: 'text-slate-500 dark:text-slate-400',
  blue: 'text-blue-700 dark:text-blue-400',
  indigo: 'text-indigo-700 dark:text-indigo-400',
  amber: 'text-amber-700 dark:text-amber-400',
  orange: 'text-orange-700 dark:text-orange-400',
  emerald: 'text-emerald-700 dark:text-emerald-400',
  teal: 'text-teal-700 dark:text-teal-400',
  red: 'text-red-700 dark:text-red-400',
} as const

/** The same palette as a CLICKABLE surface: the chip plus its hover pair —
 *  one further notch (light -200/-400/-900, dark -800/-600/-100). */
export const CHIP_BUTTON = {
  neutral: `${CHIP.neutral} hover:bg-slate-200 hover:border-slate-400 hover:text-slate-900 dark:hover:bg-slate-700 dark:hover:border-slate-500 dark:hover:text-slate-100`,
  neutralDim: `${CHIP.neutralDim} hover:bg-slate-200 hover:border-slate-400 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:border-slate-500 dark:hover:text-slate-200`,
  blue: `${CHIP.blue} hover:bg-blue-200 hover:border-blue-400 hover:text-blue-900 dark:hover:bg-blue-800 dark:hover:border-blue-600 dark:hover:text-blue-100`,
  indigo: `${CHIP.indigo} hover:bg-indigo-200 hover:border-indigo-400 hover:text-indigo-900 dark:hover:bg-indigo-800 dark:hover:border-indigo-600 dark:hover:text-indigo-100`,
  amber: `${CHIP.amber} hover:bg-amber-200 hover:border-amber-400 hover:text-amber-900 dark:hover:bg-amber-800 dark:hover:border-amber-600 dark:hover:text-amber-100`,
  orange: `${CHIP.orange} hover:bg-orange-200 hover:border-orange-400 hover:text-orange-900 dark:hover:bg-orange-800 dark:hover:border-orange-600 dark:hover:text-orange-100`,
  green: `${CHIP.green} hover:bg-green-200 hover:border-green-400 hover:text-green-900 dark:hover:bg-green-800 dark:hover:border-green-600 dark:hover:text-green-100`,
  emerald: `${CHIP.emerald} hover:bg-emerald-200 hover:border-emerald-400 hover:text-emerald-900 dark:hover:bg-emerald-800 dark:hover:border-emerald-600 dark:hover:text-emerald-100`,
  teal: `${CHIP.teal} hover:bg-teal-200 hover:border-teal-400 hover:text-teal-900 dark:hover:bg-teal-800 dark:hover:border-teal-600 dark:hover:text-teal-100`,
} as const

/** THE index-row state mark (design 169, 2026-10-13): a dot + the word, no
 *  surface. Six tones by MEANING — `StateMark` is the only reader. The dim
 *  tone is a HOLLOW dot (a ring) with a muted, medium-weight word: dead states
 *  recede instead of wearing a colour. Solid light + `dark:` pairs, no opacity.
 *  Teal rides the theme's own state tokens (`--state` / `--state-ink`), so the
 *  mark says « in hand » in the same teal the nav disc and the focus ring do. */
export type MarkTone = 'neutral' | 'teal' | 'amber' | 'emerald' | 'red' | 'dim'

export const MARK: Record<MarkTone, { dot: string; text: string }> = {
  neutral: { dot: 'bg-slate-400 dark:bg-slate-500',              text: 'font-semibold text-foreground' },
  teal:    { dot: 'bg-state',                                    text: 'font-semibold text-state-ink' },
  amber:   { dot: 'bg-tone-amber',                               text: 'font-semibold text-tone-amber' },
  emerald: { dot: 'bg-tone-emerald',                             text: 'font-semibold text-tone-emerald' },
  red:     { dot: 'bg-destructive',                              text: 'font-semibold text-destructive' },
  dim:     { dot: 'ring-[1.5px] ring-inset ring-slate-400 dark:ring-slate-500', text: 'font-medium text-muted-foreground' },
}
